import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "../../axiosConfig";
import TrainerLayout from "./TrainerLayout";
import { TrainerContext } from "../../context/TrainerContext";
import {
    FiEdit2,
    FiSave,
    FiX,
    FiPlus,
    FiChevronLeft,
    FiUsers,
    FiSearch,
    FiArrowLeft,
    FiCalendar
} from "react-icons/fi";
import DeleteConfirmationModal from "./DeleteGroupTrainingModal";
import GroupTrainings from "./GroupTrainings";
import "../../styles/TrainerGroupDetails.css"

const TrainerGroupDetails = () => {
    const { groupId } = useParams();
    const { trainer } = useContext(TrainerContext);
    const navigate = useNavigate();

    // Fetched group details
    const [groupDetails, setGroupDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    // Edit mode state
    const [editMode, setEditMode] = useState(false);
    const [editedName, setEditedName] = useState("");
    const [editedDescription, setEditedDescription] = useState("");
    const [editedTrainerIds, setEditedTrainerIds] = useState([]);
    const [editedDogIds, setEditedDogIds] = useState([]);

    // Modal state for adding trainers/dogs
    const [isTrainerModalOpen, setIsTrainerModalOpen] = useState(false);
    const [isDogModalOpen, setIsDogModalOpen] = useState(false);
    const [modalTrainerSearch, setModalTrainerSearch] = useState("");
    const [modalDogSearch, setModalDogSearch] = useState("");
    const [allTrainers, setAllTrainers] = useState([]);
    const [allDogs, setAllDogs] = useState([]);

    // Delete confirmation modal state
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    // Fetch group details when component mounts
    useEffect(() => {
        const fetchGroupDetails = async () => {
            try {
                const token = localStorage.getItem("jwt");
                const response = await axios.get(`/api/v1/groups/${groupId}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });

                setGroupDetails(response.data);
                setEditedName(response.data.name);
                setEditedDescription(response.data.description);
                setEditedTrainerIds(
                    response.data.groupTrainers ? response.data.groupTrainers.map(t => t.id) : []
                );
                setEditedDogIds(
                    response.data.groupDogs ? response.data.groupDogs.map(d => d.id) : []
                );
                setLoading(false);
            } catch (err) {
                setError("Błąd pobierania szczegółów grupy.");
                setLoading(false);
            }
        };

        fetchGroupDetails();
    }, [groupId]);

    // Open modal to add trainers: fetch all trainers for the school
    const openTrainerModal = async () => {
        try {
            const token = localStorage.getItem("jwt");
            const response = await axios.get(`/api/v1/trainer/find-all/${trainer.schoolId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setAllTrainers(response.data || []);
            setIsTrainerModalOpen(true);
        } catch (err) {
            showNotification("Błąd pobierania trenerów.", "error");
        }
    };

    // Open modal to add dogs: fetch dogs for booking for the school
    const openDogModal = async () => {
        try {
            const token = localStorage.getItem("jwt");
            const response = await axios.get(`/api/v1/dog/list/${trainer.schoolId}/booking`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setAllDogs(response.data || []);
            setIsDogModalOpen(true);
        } catch (err) {
            showNotification("Błąd pobierania pupili.", "error");
        }
    };

    // Helper to show notifications
    const showNotification = (message, type = "success") => {
        const notification = document.createElement("div");
        notification.className = type === "success" ? "success-notification" : "error-notification";
        notification.textContent = message;
        document.body.appendChild(notification);
        setTimeout(() => {
            document.body.removeChild(notification);
        }, 3000);
    };

    // Filter modal trainers by search term
    const filteredModalTrainers = allTrainers.filter((t) => {
        const term = modalTrainerSearch.toLowerCase();
        const fullName = `${t.firstName} ${t.lastName}`.toLowerCase();
        return fullName.includes(term);
    });

    // Filter modal dogs by search term
    const filteredModalDogs = allDogs.filter((d) => {
        const term = modalDogSearch.toLowerCase();
        const info = `${d.name} ${d.breed} ${d.ownersName}`.toLowerCase();
        return info.includes(term);
    });

    // Save changes: compute differences and call multiple endpoints
    const handleSaveEdits = async () => {
        if (!editedName.trim()) {
            showNotification("Nazwa grupy nie może być pusta.", "error");
            return;
        }

        try {
            const token = localStorage.getItem("jwt");
            const headers = { Authorization: `Bearer ${token}` };

            // Calculate diffs
            const initialTrainerIds = groupDetails.groupTrainers
                ? groupDetails.groupTrainers.map(t => t.id)
                : [];
            const initialDogIds = groupDetails.groupDogs
                ? groupDetails.groupDogs.map(d => d.id)
                : [];

            const trainersToAdd = editedTrainerIds.filter(id => !initialTrainerIds.includes(id));
            const trainersToRemove = initialTrainerIds.filter(id => !editedTrainerIds.includes(id));
            const dogsToAdd = editedDogIds.filter(id => !initialDogIds.includes(id));
            const dogsToRemove = initialDogIds.filter(id => !editedDogIds.includes(id));

            // Update group name and description
            await axios.put(`/api/v1/groups/${groupId}`, {
                name: editedName,
                description: editedDescription,
            }, { headers });

            // Add new trainers
            if (trainersToAdd.length > 0) {
                await axios.post(`/api/v1/groups/${groupId}/trainers`, {
                    memberIds: trainersToAdd
                }, { headers });
            }

            // Remove trainers
            for (const trainerId of trainersToRemove) {
                await axios.delete(`/api/v1/groups/${groupId}/trainers/${trainerId}`, { headers });
            }

            // Add new dogs
            if (dogsToAdd.length > 0) {
                await axios.post(`/api/v1/groups/${groupId}/dogs`, {
                    memberIds: dogsToAdd
                }, { headers });
            }

            // Remove dogs
            for (const dogId of dogsToRemove) {
                await axios.delete(`/api/v1/groups/${groupId}/dogs/${dogId}`, { headers });
            }

            // Refresh group details
            const response = await axios.get(`/api/v1/groups/${groupId}`, { headers });
            setGroupDetails(response.data);
            setEditMode(false);
            showNotification("Zmiany zapisane pomyślnie.");
        } catch (err) {
            const errorMsg = err.response?.data?.message || "Błąd przy zapisywaniu zmian.";
            showNotification(errorMsg, "error");
        }
    };

    // Handle deleting the entire group
    const handleDeleteGroup = async () => {
        setIsDeleting(true);

        try {
            const token = localStorage.getItem("jwt");
            await axios.delete(`/api/v1/groups/${groupId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            showNotification("Grupa została usunięta.");
            navigate("/trainer/groups");
        } catch (err) {
            const errorMsg = err.response?.data?.message || "Błąd przy usuwaniu grupy.";
            showNotification(errorMsg, "error");
            setIsDeleting(false);
            setIsDeleteModalOpen(false);
        }
    };

    // Cancel edit mode
    const cancelEdit = () => {
        setEditedName(groupDetails.name);
        setEditedDescription(groupDetails.description);
        setEditedTrainerIds(groupDetails.groupTrainers ? groupDetails.groupTrainers.map(t => t.id) : []);
        setEditedDogIds(groupDetails.groupDogs ? groupDetails.groupDogs.map(d => d.id) : []);
        setEditMode(false);
    };

    if (loading) {
        return (
            <TrainerLayout>
                <div className="modern-loading">
                    <div className="modern-spinner"></div>
                    <p>Ładowanie...</p>
                </div>
            </TrainerLayout>
        );
    }

    if (error) {
        return (
            <TrainerLayout>
                <div className="modern-error">{error}</div>
            </TrainerLayout>
        );
    }

    if (!groupDetails) return null;

    return (
        <TrainerLayout>
            <div className="modern-group-details">
                <div className="modern-details-header">
                    <button
                        className="modern-back-button"
                        onClick={() => navigate("/trainer/groups")}
                    >
                        <FiArrowLeft />
                        <span>Powrót do grup</span>
                    </button>

                    {!editMode ? (
                        <div className="modern-actions">
                            <button
                                className="modern-edit-button"
                                onClick={() => setEditMode(true)}
                            >
                                <FiEdit2 className="btn-icon" />
                                <span>Edytuj</span>
                            </button>
                            <button
                                className="modern-delete-button"
                                onClick={() => setIsDeleteModalOpen(true)}
                            >
                                <FiX className="btn-icon" />
                                <span>Usuń</span>
                            </button>
                        </div>
                    ) : (
                        <div className="modern-actions">
                            <button
                                className="modern-cancel-button"
                                onClick={cancelEdit}
                            >
                                <FiX className="btn-icon" />
                                <span>Anuluj</span>
                            </button>
                            <button
                                className="modern-save-button"
                                onClick={handleSaveEdits}
                            >
                                <FiSave className="btn-icon" />
                                <span>Zapisz</span>
                            </button>
                        </div>
                    )}
                </div>

                <div className="modern-details-content">
                    {/* GROUP DETAILS SECTION */}
                    {!editMode && (
                        <>
                            <div className="modern-details-section">
                                <h2 className="modern-group-name">{groupDetails.name}</h2>
                                <p className="modern-group-description">
                                    {groupDetails.description || "Brak opisu"}
                                </p>
                            </div>

                            <div className="modern-details-section">
                                <h3 className="modern-section-title">
                                    <FiUsers className="section-icon" />
                                    <span>Trenerzy</span>
                                    <span className="modern-count">
                                        {groupDetails.groupTrainers?.length || 0}
                                    </span>
                                </h3>

                                {groupDetails.groupTrainers && groupDetails.groupTrainers.length > 0 ? (
                                    <div className="modern-members-list">
                                        {groupDetails.groupTrainers.map((trainer) => (
                                            <div key={trainer.id} className="modern-member-card">
                                                <div className="modern-avatar">
                                                    {trainer.firstName.charAt(0)}
                                                    {trainer.lastName.charAt(0)}
                                                </div>
                                                <div className="modern-member-info">
                                                    <span className="modern-member-name">
                                                        {trainer.firstName} {trainer.lastName}
                                                    </span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="modern-empty-list">
                                        Brak trenerów w grupie
                                    </div>
                                )}
                            </div>

                            <div className="modern-details-section">
                                <h3 className="modern-section-title">
                                    <FiUsers className="section-icon" />
                                    <span>Pupile</span>
                                    <span className="modern-count">
                                        {groupDetails.groupDogs?.length || 0}
                                    </span>
                                </h3>

                                {groupDetails.groupDogs && groupDetails.groupDogs.length > 0 ? (
                                    <div className="modern-members-list">
                                        {groupDetails.groupDogs.map((dog) => (
                                            <div key={dog.id} className="modern-member-card">
                                                <div className="modern-avatar dog-avatar">
                                                    {dog.name.charAt(0)}
                                                </div>
                                                <div className="modern-member-info">
                                                    <span className="modern-member-name">{dog.name}</span>
                                                    <span className="modern-member-details">
                                                        {dog.breed && <span>{dog.breed}</span>}
                                                        {dog.ownersName && <span>Właściciel: {dog.ownersName}</span>}
                                                    </span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="modern-empty-list">
                                        Brak pupili w grupie
                                    </div>
                                )}
                            </div>
                        </>
                    )}

                    {/* EDIT MODE CONTENT */}
                    {editMode && (
                        <>
                            <div className="modern-edit-form">
                                <div className="modern-input-group">
                                    <label htmlFor="editName">Nazwa grupy</label>
                                    <input
                                        id="editName"
                                        type="text"
                                        value={editedName}
                                        onChange={(e) => setEditedName(e.target.value)}
                                        placeholder="Podaj nazwę grupy"
                                    />
                                </div>

                                <div className="modern-input-group">
                                    <label htmlFor="editDesc">Opis grupy</label>
                                    <textarea
                                        id="editDesc"
                                        value={editedDescription}
                                        onChange={(e) => setEditedDescription(e.target.value)}
                                        placeholder="Podaj opis grupy (opcjonalnie)"
                                        rows="3"
                                    />
                                </div>
                            </div>

                            <div className="modern-edit-section">
                                <div className="modern-section-header">
                                    <h3>Trenerzy</h3>
                                    <button
                                        className="modern-add-button"
                                        onClick={openTrainerModal}
                                    >
                                        <FiPlus className="btn-icon" />
                                        <span>Dodaj trenera</span>
                                    </button>
                                </div>

                                {editedTrainerIds.length > 0 ? (
                                    <div className="modern-edit-list">
                                        {editedTrainerIds.map(id => {
                                            // Try to find trainer in groupDetails first, then in allTrainers
                                            let trainer = groupDetails.groupTrainers?.find(t => t.id === id);
                                            if (!trainer) {
                                                trainer = allTrainers.find(t => t.id === id);
                                            }

                                            return (
                                                <div key={id} className="modern-edit-item">
                                                    <div className="modern-item-info">
                                                        <div className="modern-avatar small">
                                                            {trainer?.firstName?.charAt(0) || '?'}
                                                            {trainer?.lastName?.charAt(0) || '?'}
                                                        </div>
                                                        <span>
                                                            {trainer
                                                                ? `${trainer.firstName} ${trainer.lastName}`
                                                                : `Trener (ID: ${id})`}
                                                        </span>
                                                    </div>
                                                    <button
                                                        className="modern-remove-btn"
                                                        onClick={() => setEditedTrainerIds(
                                                            editedTrainerIds.filter(trainerId => trainerId !== id)
                                                        )}
                                                    >
                                                        <FiX />
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="modern-empty-edit-list">
                                        Brak wybranych trenerów
                                    </div>
                                )}
                            </div>

                            <div className="modern-edit-section">
                                <div className="modern-section-header">
                                    <h3>Pupile</h3>
                                    <button
                                        className="modern-add-button"
                                        onClick={openDogModal}
                                    >
                                        <FiPlus className="btn-icon" />
                                        <span>Dodaj pupila</span>
                                    </button>
                                </div>

                                {editedDogIds.length > 0 ? (
                                    <div className="modern-edit-list">
                                        {editedDogIds.map(id => {
                                            // Try to find dog in groupDetails first, then in allDogs
                                            let dog = groupDetails.groupDogs?.find(d => d.id === id);
                                            if (!dog) {
                                                dog = allDogs.find(d => d.id === id);
                                            }

                                            return (
                                                <div key={id} className="modern-edit-item">
                                                    <div className="modern-item-info">
                                                        <div className="modern-avatar small dog-avatar">
                                                            {dog?.name?.charAt(0) || '?'}
                                                        </div>
                                                        <div>
                                                            <span className="item-name">
                                                                {dog ? dog.name : `Pupil (ID: ${id})`}
                                                            </span>
                                                            {dog && (
                                                                <span className="item-details">
                                                                    {dog.breed && <span>{dog.breed}</span>}
                                                                    {dog.ownersName && <span>{dog.ownersName}</span>}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <button
                                                        className="modern-remove-btn"
                                                        onClick={() => setEditedDogIds(
                                                            editedDogIds.filter(dogId => dogId !== id)
                                                        )}
                                                    >
                                                        <FiX />
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="modern-empty-edit-list">
                                        Brak wybranych pupili
                                    </div>
                                )}
                            </div>
                        </>
                    )}

                    {/* TRAININGS SECTION - Always shown when not in edit mode */}
                    {!editMode && (
                        <div className="modern-trainings-section">
                            <GroupTrainings groupId={groupId} />
                        </div>
                    )}

                    {/* Message when in edit mode */}
                    {editMode && (
                        <div className="modern-trainings-section">
                            <div className="modern-section-divider">
                                <h3 className="modern-section-title">
                                    <FiCalendar className="section-icon" />
                                    <span>Treningi grupy</span>
                                </h3>
                            </div>
                            <div className="modern-details-section">
                                <div className="modern-empty-list">
                                    Aby zarządzać treningami, zakończ tryb edycji grupy.
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Modal for selecting trainers */}
                {isTrainerModalOpen && (
                    <div className="modern-modal-overlay">
                        <div className="modern-modal">
                            <div className="modern-modal-header">
                                <button
                                    className="modern-back-btn"
                                    onClick={() => setIsTrainerModalOpen(false)}
                                >
                                    <FiChevronLeft />
                                </button>
                                <h2 className="modern-modal-title">Wybierz trenerów</h2>
                                <button
                                    className="modern-close-btn"
                                    onClick={() => setIsTrainerModalOpen(false)}
                                >
                                    &times;
                                </button>
                            </div>

                            <div className="modern-modal-content">
                                <div className="modern-search-box">
                                    <FiSearch className="search-icon" />
                                    <input
                                        type="text"
                                        placeholder="Wyszukaj trenera..."
                                        value={modalTrainerSearch}
                                        onChange={(e) => setModalTrainerSearch(e.target.value)}
                                    />
                                </div>

                                <div className="modern-selection-list">
                                    {filteredModalTrainers.length === 0 ? (
                                        <p className="modern-empty-selection">Brak trenerów.</p>
                                    ) : (
                                        filteredModalTrainers.map((t) => (
                                            <div key={t.id} className="modern-selection-item">
                                                <label className="modern-checkbox">
                                                    <input
                                                        type="checkbox"
                                                        checked={editedTrainerIds.includes(t.id)}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                setEditedTrainerIds([...editedTrainerIds, t.id]);
                                                            } else {
                                                                setEditedTrainerIds(
                                                                    editedTrainerIds.filter((id) => id !== t.id)
                                                                );
                                                            }
                                                        }}
                                                    />
                                                    <span className="checkmark"></span>
                                                    <span className="name">{t.firstName} {t.lastName}</span>
                                                </label>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>

                            <div className="modern-modal-footer">
                                <button
                                    className="modern-save-btn"
                                    onClick={() => setIsTrainerModalOpen(false)}
                                >
                                    Zatwierdź wybór
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Modal for selecting dogs */}
                {isDogModalOpen && (
                    <div className="modern-modal-overlay">
                        <div className="modern-modal">
                            <div className="modern-modal-header">
                                <button
                                    className="modern-back-btn"
                                    onClick={() => setIsDogModalOpen(false)}
                                >
                                    <FiChevronLeft />
                                </button>
                                <h2 className="modern-modal-title">Wybierz pupile</h2>
                                <button
                                    className="modern-close-btn"
                                    onClick={() => setIsDogModalOpen(false)}
                                >
                                    &times;
                                </button>
                            </div>

                            <div className="modern-modal-content">
                                <div className="modern-search-box">
                                    <FiSearch className="search-icon" />
                                    <input
                                        type="text"
                                        placeholder="Wyszukaj pupila..."
                                        value={modalDogSearch}
                                        onChange={(e) => setModalDogSearch(e.target.value)}
                                    />
                                </div>

                                <div className="modern-selection-list">
                                    {filteredModalDogs.length === 0 ? (
                                        <p className="modern-empty-selection">Brak pupili.</p>
                                    ) : (
                                        filteredModalDogs.map((d) => (
                                            <div key={d.id} className="modern-selection-item">
                                                <label className="modern-checkbox">
                                                    <input
                                                        type="checkbox"
                                                        checked={editedDogIds.includes(d.id)}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                setEditedDogIds([...editedDogIds, d.id]);
                                                            } else {
                                                                setEditedDogIds(
                                                                    editedDogIds.filter((id) => id !== d.id)
                                                                );
                                                            }
                                                        }}
                                                    />
                                                    <span className="checkmark"></span>
                                                    <span className="name">
                                                        {d.name} {d.breed && `(${d.breed})`}
                                                        {d.ownersName && <span className="owner"> - {d.ownersName}</span>}
                                                    </span>
                                                </label>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>

                            <div className="modern-modal-footer">
                                <button
                                    className="modern-save-btn"
                                    onClick={() => setIsDogModalOpen(false)}
                                >
                                    Zatwierdź wybór
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Delete Confirmation Modal */}
                {isDeleteModalOpen && (
                    <DeleteConfirmationModal
                        isOpen={isDeleteModalOpen}
                        onClose={() => {
                            setIsDeleteModalOpen(false);
                        }}
                        onConfirm={handleDeleteGroup}
                        itemName={`Grupa "${groupDetails.name}"`}
                        isLoading={isDeleting}
                    />
                )}
            </div>
        </TrainerLayout>
    );
};

export default TrainerGroupDetails;